/**
 * AllGroupsTab — server-side searched and sorted directory of every group,
 * with the join CTA (mobile allGroupsTab parity).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGroup } from '@wlb/shared/context/GroupContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import useDebounce from '@wlb/shared/hooks/useDebounce';
import type { Group, GroupSortOption, GroupTypeFilter } from '@wlb/shared/types/groups';
import { ListStatus } from '../../components/ui/ListStatus';
import { GroupRow } from './GroupRow';
import { GroupRowsSkeleton } from './GroupSkeletons';
import { GroupSearchBar } from './GroupSearchBar';
import { SortGroupsModal } from './SortGroupsModal';
import { useScrollRestore } from './useScrollRestore';
import '../../components/ui/skeleton.css';
import '../feed/feed.css';
import './groups.css';

const TYPE_FILTERS: { value: GroupTypeFilter; labelKey: string }[] = [
  { value: 'all', labelKey: 'home.allGroups.filterAll' },
  { value: 'public', labelKey: 'home.allGroups.filterPublic' },
  { value: 'private', labelKey: 'home.allGroups.filterPrivate' },
];

export function AllGroupsTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    allGroups,
    allGroupsLoading,
    allGroupsError,
    allGroupsLoaded,
    allGroupsTotalCount,
    fetchAllGroups,
    joinGroup,
  } = useGroup();

  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState<GroupSortOption>('newest');
  const [typeFilter, setTypeFilter] = useState<GroupTypeFilter>('all');
  const [sortOpen, setSortOpen] = useState(false);
  const debouncedQuery = useDebounce(searchQuery.trim(), 350);

  const query = useMemo(
    () => ({
      sortBy: sortOption,
      searchTerm: debouncedQuery || undefined,
      // 'all' is the server default, so it is left off the request entirely.
      groupType: typeFilter === 'all' ? undefined : typeFilter,
    }),
    [sortOption, debouncedQuery, typeFilter],
  );

  const load = useCallback(() => {
    fetchAllGroups(query).catch(() => undefined);
  }, [fetchAllGroups, query]);

  useEffect(load, [load]);

  /** Member-initiated reload — bypasses the cache key so it always hits the API. */
  const refresh = useCallback(() => {
    fetchAllGroups(query, { force: true }).catch(() => undefined);
  }, [fetchAllGroups, query]);

  // Reset scroll to top whenever the query, sort or filter changes so a stale
  // offset from a previous result set is not restored into a shorter list.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [query]);

  useScrollRestore('groups:allGroups', !allGroupsLoading || allGroups.length > 0);

  const handleSelect = (group: Group) => navigate(`/groups/${group.id}`);
  const handleJoin = (groupId: string) => { joinGroup(groupId).catch(() => undefined); };

  const renderList = () => {
    if (allGroupsLoading && allGroups.length === 0) {
      return <GroupRowsSkeleton />;
    }
    if (allGroupsError && allGroups.length === 0) {
      return (
        <ListStatus
          variant="error"
          message={allGroupsError}
          onAction={refresh}
          busy={allGroupsLoading}
        />
      );
    }
    if (!allGroupsLoaded && allGroups.length === 0) {
      return <GroupRowsSkeleton />;
    }
    if (allGroups.length === 0) {
      // A search that matched nothing is not a stale directory, so refreshing
      // is only offered when the member is looking at the unfiltered list.
      return (
        <ListStatus
          variant="empty"
          title={t('home.allGroups.emptyList')}
          onAction={debouncedQuery ? undefined : refresh}
          busy={allGroupsLoading}
        />
      );
    }
    return (
      <>
        {allGroupsTotalCount > allGroups.length && (
          <p className="all-groups__count" role="status">
            {t('home.allGroups.resultCount', {
              shown: allGroups.length,
              total: allGroupsTotalCount,
            })}
          </p>
        )}
        <div className="all-groups__list skeleton-fade-in">
          {allGroups.map(group => (
            <GroupRow key={group.id} group={group} onSelect={handleSelect} onJoin={handleJoin} />
          ))}
        </div>
      </>
    );
  };

  return (
    <>
      <div className="my-groups__header">
        <GroupSearchBar
          inputId="all-groups-search"
          value={searchQuery}
          onChange={setSearchQuery}
          onFilter={() => setSortOpen(true)}
        />
      </div>
      <div
        className="all-groups__types"
        role="group"
        aria-label={t('home.allGroups.typeFilter')}
      >
        {TYPE_FILTERS.map(filter => (
          <button
            key={filter.value}
            type="button"
            aria-pressed={typeFilter === filter.value}
            className={`all-groups__type-chip${typeFilter === filter.value ? ' all-groups__type-chip--active' : ''}`}
            onClick={() => setTypeFilter(filter.value)}
          >
            {t(filter.labelKey)}
          </button>
        ))}
      </div>
      {renderList()}
      {sortOpen && (
        <SortGroupsModal
          selected={sortOption}
          onClose={() => setSortOpen(false)}
          onApply={setSortOption}
        />
      )}
    </>
  );
}
